import { useState, useRef, useEffect } from "react";
import { MOM_TYPES } from "@/constants/chipPools";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Check, X, ArrowRight } from "lucide-react";
import KinAvatar from "./KinAvatar";
import { useStatusBar } from "@/hooks/useStatusBar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface MomTypeSelectorProps {
  onComplete: () => void;
  /** When true, shows X close button and uses "edit" copy */
  editMode?: boolean;
  /** Pre-selected mom types for edit mode */
  initialSelection?: string[];
  onDismiss?: () => void;
}

const MomTypeSelector = ({ onComplete, editMode = false, initialSelection = [], onDismiss }: MomTypeSelectorProps) => {
  useStatusBar({ color: 'F0F0FF', style: 'dark' });
  const { user } = useAuth();
  const [selected, setSelected] = useState<string[]>(initialSelection);
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const pendingDiscardRef = useRef<(() => void) | null>(null);
  const pillRef = useRef<HTMLDivElement>(null);

  // Trigger entrance animation
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const toggle = (key: string) => {
    setHasChanges(true);
    if (selected.includes(key)) {
      setSelected(selected.filter((k) => k !== key));
    } else if (selected.length < 3) {
      setSelected([...selected, key]);
    } else {
      pillRef.current?.animate(
        [{ transform: "scale(1)" }, { transform: "scale(1.08)" }, { transform: "scale(1)" }],
        { duration: 300, easing: "ease-in-out" }
      );
    }
  };

  const animateOut = (cb: () => void) => {
    setVisible(false);
    setTimeout(cb, 300);
  };

  const handleSubmit = async () => {
    if (selected.length === 0 || !user || saving) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: user.id,
          mom_types: selected,
          mom_type_selected_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
      if (error) throw error;
      const msg = editMode ? "Updated. Fresh vibes incoming. 💜" : "Got it. Let me put this to work. 💜";
      toast(msg, {
        style: {
          background: "#FFFFFF",
          border: "1.5px solid #5050C8",
          fontFamily: "'DM Serif Display', serif",
          color: "#1C1A3E",
        },
        duration: 2000,
      });
      animateOut(() => onComplete());
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Try again!");
      setSaving(false);
    }
  };

  const handleBackdropClick = () => {
    if (!editMode && !hasChanges) return; // first-time users can't dismiss without selecting
    if (editMode && hasChanges) {
      pendingDiscardRef.current = () => animateOut(() => onDismiss?.());
      setConfirmDiscardOpen(true);
      return;
    }
    animateOut(() => onDismiss?.());
  };

  const handleClose = () => {
    if (editMode && hasChanges) {
      pendingDiscardRef.current = () => animateOut(() => onDismiss?.());
      setConfirmDiscardOpen(true);
      return;
    }
    animateOut(() => onDismiss?.());
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center transition-all duration-300"
      style={{
        backgroundColor: visible ? "rgba(28, 26, 62, 0.6)" : "rgba(28, 26, 62, 0)",
        backdropFilter: visible ? "blur(4px)" : "blur(0px)",
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full mx-4 bg-white overflow-y-auto transition-all duration-400 ease-out"
        style={{
          maxWidth: 440,
          maxHeight: "90vh",
          borderRadius: 20,
          padding: "28px 20px",
          boxShadow: "0 24px 60px rgba(28, 26, 62, 0.25)",
          opacity: visible ? 1 : 0,
          transform: visible ? "scale(1)" : "scale(0.95)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button for edit mode or existing users */}
        {(editMode || onDismiss) && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"
          >
            <X size={18} color="#54525C" />
          </button>
        )}

        <div className="flex flex-col items-center">
          {/* K icon */}
          <div className="animate-float mb-4">
            <KinAvatar size="md" variant="dark" />
          </div>

          {/* Heading */}
          {editMode ? (
            <h1
              className="text-center"
              style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: "#1C1A3E", marginBottom: 4 }}
            >
              Update your vibe
            </h1>
          ) : (
            <>
              <h1
                className="text-center"
                style={{ fontFamily: "'DM Serif Display', serif", fontSize: 24, color: "#1C1A3E", marginBottom: 4 }}
              >
                Hey mama.
              </h1>
              <p
                className="text-center"
                style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 20, color: "#5050C8", marginBottom: 12 }}
              >
                Before we get started...
              </p>
            </>
          )}

          <p
            className="text-center"
            style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: "#1C1A3E", marginBottom: 4 }}
          >
            What kind of mom are you?
          </p>
          <p
            className="text-center"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, color: "#54525C" }}
          >
            Pick up to 3. I'll personalize Kin for you.
          </p>
          <p
            className="text-center"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, fontStyle: "italic", color: "#54525C", marginTop: 4, marginBottom: 16 }}
          >
            No pressure, you can change these anytime in settings. 💜
          </p>

          {/* Cards */}
          <div className="flex flex-col gap-2.5 w-full">
            {MOM_TYPES.map((mom, i) => {
              const isSelected = selected.includes(mom.key);
              return (
                <button
                  key={mom.key}
                  onClick={() => toggle(mom.key)}
                  className="flex items-center gap-3 w-full text-left transition-all duration-200"
                  style={{
                    padding: 16,
                    borderRadius: 16,
                    border: isSelected ? "2px solid #5050C8" : "1.5px solid #C8C8F4",
                    background: isSelected
                      ? "linear-gradient(to right, #FFFFFF, rgba(80,80,200,0.04))"
                      : "#FFFFFF",
                  }}
                >
                  <span style={{ fontSize: 24, flexShrink: 0 }}>{mom.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p style={{ fontFamily: "'DM Serif Display', serif", fontSize: 15, color: "#1C1A3E" }}>
                      {mom.title}
                    </p>
                    <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12.5, color: "#54525C", marginTop: 2 }}>
                      {mom.description}
                    </p>
                  </div>
                  <div
                    className="flex-shrink-0 flex items-center justify-center transition-all duration-200"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: isSelected ? "none" : "1.5px solid #C8C8F4",
                      backgroundColor: isSelected ? "#5050C8" : "transparent",
                    }}
                  >
                    {isSelected && <Check size={14} color="#FFFFFF" strokeWidth={3} />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Counter pill */}
          {selected.length > 0 && (
            <div
              ref={pillRef}
              className="mt-4"
              style={{
                backgroundColor: "#5050C8",
                borderRadius: 20,
                padding: "6px 16px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 13,
                color: "#FFFFFF",
              }}
            >
              {selected.length} of 3 selected
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={selected.length === 0 || saving}
            className="w-full mt-4 transition-all duration-200 flex items-center justify-center"
            style={{
              height: 48,
              borderRadius: 14,
              backgroundColor: selected.length > 0 ? "#5050C8" : "#C8C8F4",
              color: "#FFFFFF",
              fontFamily: "'DM Serif Display', serif",
              fontSize: 17,
              border: "none",
              cursor: selected.length > 0 && !saving ? "pointer" : "not-allowed",
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? (
              "Tucking it away..."
            ) : editMode ? (
              "Save changes"
            ) : (
              <>
                Let's go
                <ArrowRight size={16} style={{ marginLeft: 6, strokeWidth: 2 }} />
              </>
            )}
          </button>
        </div>
      </div>

      <AlertDialog open={confirmDiscardOpen} onOpenChange={setConfirmDiscardOpen}>
        <AlertDialogContent
          style={{
            backgroundColor: "#F0F0FF",
            border: "1.5px solid #C8C8F4",
            borderRadius: 20,
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              style={{
                fontFamily: "'DM Serif Display', serif",
                color: "#1C1A3E",
                fontSize: 22,
              }}
            >
              Toss your changes?
            </AlertDialogTitle>
            <AlertDialogDescription
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: "#54525C",
                fontSize: 14,
              }}
            >
              You haven't saved yet. Close now and your edits go poof.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              style={{
                backgroundColor: "transparent",
                border: "1.5px solid #C8C8F4",
                color: "#54525C",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
              }}
            >
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const fn = pendingDiscardRef.current;
                pendingDiscardRef.current = null;
                fn?.();
              }}
              style={{
                backgroundColor: "#5050C8",
                color: "#FFFFFF",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MomTypeSelector;
